import { useState } from "react";
import { Globe, Server } from "lucide-react";
import { getUptime } from "../api/client.js";
import type {
  MonitorStatus,
  UptimeIncident,
  UptimeMonitor,
  UptimeRegion,
  UptimeResponse,
} from "../api/types.js";
import { useApi } from "../lib/useApi.js";
import { Card, Chip, EmptyState, Pill, StatusDot } from "../components/ui.js";
import type { Tone } from "../components/ui.js";
import { AreaSparkline } from "../components/charts.js";
import { ErrorState, LoadingState } from "../components/common.js";

export interface UptimePageProps {
  refreshKey: number;
}

type Range = "24h" | "7d" | "90d";

const GRID = "minmax(0,1.7fr) 116px minmax(0,1.5fr) 86px 128px";
const GRID_GAP = "0 14px";
const TABLE_MIN_WIDTH = 960;

const RANGE_SUBTITLE: Record<Range, string> = {
  "24h": "last 24 hours · hourly",
  "7d": "last 7 days · daily",
  "90d": "last 90 days · daily",
};

const TONE_DOT: Record<Tone, string> = {
  alarm: "var(--tone-alarm-dot)",
  ok: "var(--tone-ok-dot)",
  warn: "var(--tone-warn-dot)",
  nodata: "var(--tone-nodata-dot)",
  info: "var(--tone-info-dot)",
  accent: "var(--accent-dot)",
};

const TONE_FG: Record<Tone, string> = {
  alarm: "var(--tone-alarm-fg)",
  ok: "var(--tone-ok-fg)",
  warn: "var(--tone-warn-fg)",
  nodata: "var(--fg-subtle)",
  info: "var(--tone-info-fg)",
  accent: "var(--accent)",
};

const PAGE_STYLES = `.uptime-row:hover { background: var(--bg-subtle); }`;

function statusTone(status: MonitorStatus | string): Tone {
  if (status === "up") return "ok";
  if (status === "degraded") return "warn";
  if (status === "down") return "alarm";
  return "nodata";
}

function statusLabel(status: MonitorStatus | string): string {
  if (status === "up") return "OPERATIONAL";
  if (status === "degraded") return "DEGRADED";
  if (status === "down") return "DOWN";
  return status.toUpperCase();
}

function uptimeTone(pct: number, sla: number): Tone {
  if (pct >= sla) return "ok";
  if (pct >= 99.5) return "warn";
  return "alarm";
}

function worstTone(statuses: string[]): Tone {
  if (statuses.includes("down")) return "alarm";
  if (statuses.includes("degraded")) return "warn";
  return "ok";
}

function envTone(env: string): Tone {
  const e = env.toLowerCase();
  if (e === "cdn") return "info";
  if (e === "beta") return "warn";
  if (e === "staging") return "nodata";
  return "ok";
}

function averageSeries(series: number[][]): number[] {
  const len = Math.min(...series.map((s) => s.length));
  const out: number[] = [];
  for (let i = 0; i < len; i++) {
    let sum = 0;
    for (const s of series) sum += s[i];
    out.push(sum / series.length);
  }
  return out;
}

// Horizontal run of status cells; a single-element history renders as one full-width
// current-status band (used for real monitors that have no stored history).
function StatusBars({ statuses, height = 26 }: { statuses: string[]; height?: number }) {
  const gap = 2;
  const bw = 6;
  const n = statuses.length;
  if (n === 0) return null;
  const total = n * bw + (n - 1) * gap;
  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${total} ${height}`}
      preserveAspectRatio="none"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      {statuses.map((s, i) => (
        <rect
          key={i}
          x={i * (bw + gap)}
          y={0}
          width={bw}
          height={height}
          rx={1.5}
          fill={TONE_DOT[statusTone(s)]}
        />
      ))}
    </svg>
  );
}

interface SummaryStat {
  label: string;
  value: string;
  color: string;
}

function buildSummary(data: UptimeResponse, range: Range): SummaryStat[] {
  const { slaTarget, summary } = data;
  const up = summary.uptimePct;
  const allowedMin = ((100 - slaTarget) / 100) * 30 * 24 * 60;

  let budgetValue = "—";
  let budgetColor = "var(--fg-subtle)";
  if (up !== null) {
    const usedMin = Math.min(allowedMin, ((100 - up) / 100) * 30 * 24 * 60);
    const leftMin = Math.max(0, allowedMin - usedMin);
    budgetValue = `${leftMin.toFixed(0)} of ${allowedMin.toFixed(0)} min left`;
    budgetColor = leftMin > 10 ? TONE_FG.ok : TONE_FG.warn;
  }

  return [
    {
      label: `Uptime · ${range}`,
      value: up === null ? "—" : `${up.toFixed(3)}%`,
      color: up === null ? "var(--fg-subtle)" : up >= slaTarget ? TONE_FG.ok : TONE_FG.warn,
    },
    { label: "SLA target", value: `${slaTarget}%`, color: "var(--fg)" },
    { label: "Error budget · 30d", value: budgetValue, color: budgetColor },
    {
      label: "Active incidents",
      value: summary.activeIncidents === 0 ? "0 · all clear" : `${summary.activeIncidents} active`,
      color: summary.activeIncidents === 0 ? TONE_FG.ok : TONE_FG.alarm,
    },
    { label: "Monitors", value: `${summary.monitorCount} checks`, color: "var(--fg)" },
  ];
}

function SummaryStrip({ stats }: { stats: SummaryStat[] }) {
  return (
    <Card style={{ display: "flex", alignItems: "center", gap: 26, flexWrap: "wrap", padding: "14px 20px" }}>
      {stats.map((s) => (
        <span key={s.label} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <span className="label--table">{s.label}</span>
          <span className="mono" style={{ fontSize: 15, fontWeight: 500, color: s.color }}>
            {s.value}
          </span>
        </span>
      ))}
    </Card>
  );
}

function RegionCard({ region }: { region: UptimeRegion }) {
  const Icon = region.region === "edge" ? Globe : Server;
  return (
    <Card tight style={{ display: "flex", flexDirection: "column", gap: 11, padding: "15px 17px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <span
          style={{
            width: 28,
            height: 28,
            flex: "none",
            borderRadius: 8,
            background: "var(--bg-subtle)",
            color: "var(--fg-subtle)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={15} aria-hidden="true" />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "var(--fg)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {region.label}
          </span>
          <span style={{ fontSize: 11, color: "var(--fg-subtle)" }}>{region.monitorCount} monitors</span>
        </span>
        <span className="mono" style={{ fontSize: 13, fontWeight: 500, color: "var(--fg)" }}>
          {region.uptimePct === null ? "—" : `${region.uptimePct.toFixed(2)}%`}
        </span>
      </div>
      <StatusBars statuses={region.history} height={20} />
    </Card>
  );
}

function HeaderCell({ label, right }: { label: string; right?: boolean }) {
  return <span className="label--table" style={right ? { textAlign: "right" } : undefined}>{label}</span>;
}

function MonitorRow({ monitor, sla }: { monitor: UptimeMonitor; sla: number }) {
  const m = monitor;
  const uptimeText =
    m.uptimePct !== null
      ? `${m.uptimePct.toFixed(2)}%`
      : m.responseMs !== null
        ? `${m.responseMs} ms live`
        : "—";
  const uptimeColor =
    m.uptimePct !== null ? TONE_FG[uptimeTone(m.uptimePct, sla)] : "var(--fg-subtle)";
  const historyCells = m.history ?? [m.status];

  return (
    <div
      className="uptime-row"
      style={{
        display: "grid",
        gridTemplateColumns: GRID,
        gap: GRID_GAP,
        alignItems: "center",
        padding: "11px 20px",
        borderBottom: "1px solid var(--divider)",
      }}
    >
      <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--fg)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {m.name}
          </span>
          <Pill tone={envTone(m.env)}>{m.env.toUpperCase()}</Pill>
        </span>
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--fg-subtle)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {m.tenant ? `${m.tenant} · ${m.endpoint}` : m.endpoint}
        </span>
      </span>

      <span style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
        <Pill tone={statusTone(m.status)}>
          <StatusDot tone={statusTone(m.status)} size={5} />
          {statusLabel(m.status)}
        </Pill>
        <span className="mono" style={{ fontSize: 12, color: uptimeColor }}>
          {uptimeText}
        </span>
      </span>

      <span style={{ display: "block", minWidth: 0 }}>
        <StatusBars statuses={historyCells} height={26} />
      </span>

      <span
        className="mono"
        style={{ fontSize: 11.5, color: "var(--fg-subtle)", textAlign: "right", lineHeight: 1.5 }}
      >
        {m.p50Ms !== null ? (
          <>
            {m.p50Ms} ms
            <br />
            {m.p95Ms !== null ? `${m.p95Ms} ms` : "—"}
          </>
        ) : (
          "—"
        )}
      </span>

      <span style={{ display: "flex", justifyContent: "flex-end" }}>
        {m.responseSeries && m.responseSeries.length > 0 ? (
          <AreaSparkline points={m.responseSeries} width={116} height={26} color="var(--tone-ok-dot)" />
        ) : null}
      </span>
    </div>
  );
}

function MonitorsCard({
  monitors,
  sla,
  range,
}: {
  monitors: UptimeMonitor[];
  sla: number;
  range: Range;
}) {
  return (
    <Card flush>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          padding: "14px 20px 10px",
        }}
      >
        <span className="display" style={{ fontSize: 14.5 }}>
          Monitors
        </span>
        <span style={{ fontSize: 11.5, color: "var(--fg-faint)" }}>
          status bars · {RANGE_SUBTITLE[range]} · SLA {sla}%
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: TABLE_MIN_WIDTH }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: GRID,
              gap: GRID_GAP,
              padding: "7px 20px",
              borderTop: "1px solid var(--divider)",
              borderBottom: "1px solid var(--divider)",
              background: "var(--bg-subtle)",
            }}
          >
            <HeaderCell label="Endpoint" />
            <HeaderCell label="Uptime" />
            <HeaderCell label="History" />
            <HeaderCell label="p50 · p95" right />
            <HeaderCell label="Response" right />
          </div>
          {monitors.length === 0 ? (
            <div style={{ padding: 22, textAlign: "center", fontSize: 12, color: "var(--fg-subtle)" }}>
              No monitors in this region.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {monitors.map((m, i) => (
                <MonitorRow key={`${m.endpoint}-${m.env}-${i}`} monitor={m} sla={sla} />
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function ResponseCard({ series }: { series: number[] }) {
  return (
    <Card style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span className="display" style={{ fontSize: 14.5 }}>
          Response time — p95
        </span>
        <span className="label--table">p95 · backend APIs</span>
      </div>
      <AreaSparkline
        points={series}
        height={150}
        color="var(--accent-dot)"
        showAxis
        hover
        unit=" ms"
        formatValue={(v) => `${Math.round(v)}`}
      />
    </Card>
  );
}

function IncidentRow({ incident, regionLabel }: { incident: UptimeIncident; regionLabel: string }) {
  const sevTone: Tone = incident.severity === "major" ? "alarm" : "warn";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        paddingBottom: 12,
        borderBottom: "1px solid var(--divider)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Pill tone={sevTone}>{incident.severity.toUpperCase()}</Pill>
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: "var(--fg)",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {incident.title}
        </span>
        <Pill tone={incident.resolved ? "nodata" : "alarm"}>
          {incident.resolved ? "RESOLVED" : "ACTIVE"}
        </Pill>
      </div>
      <span style={{ fontSize: 11, color: "var(--fg-faint)" }}>
        {regionLabel} · {incident.when} · {incident.durationMin} min
      </span>
      <span style={{ fontSize: 11.5, lineHeight: 1.5, color: "var(--fg-subtle)" }}>{incident.note}</span>
    </div>
  );
}

function IncidentsCard({
  incidents,
  activeIncidents,
  regionLabels,
}: {
  incidents: UptimeIncident[];
  activeIncidents: number;
  regionLabels: Map<string, string>;
}) {
  const activeTone: Tone = activeIncidents === 0 ? "ok" : "alarm";
  return (
    <Card style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className="display" style={{ fontSize: 14.5 }}>
          Incidents
        </span>
        <Pill tone={activeTone}>{activeIncidents} ACTIVE</Pill>
        <span
          style={{
            marginLeft: "auto",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "var(--fg-faint)",
          }}
        >
          last 90 days
        </span>
      </div>
      {incidents.length === 0 ? (
        <EmptyState
          title="No incidents"
          message="All clear across the last 90 days."
          tone="ok"
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {incidents.map((incident, i) => (
            <IncidentRow
              key={`${incident.title}-${i}`}
              incident={incident}
              regionLabel={regionLabels.get(incident.region) ?? incident.region}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

export function UptimePage({ refreshKey }: UptimePageProps) {
  const { data, loading, error } = useApi<UptimeResponse>(getUptime, [], refreshKey);
  const [scope, setScope] = useState<string>("all");
  const [range, setRange] = useState<Range>("7d");

  if (loading && !data) {
    return (
      <Card>
        <LoadingState label="Loading uptime" />
      </Card>
    );
  }
  if (error && !data) {
    return (
      <Card>
        <ErrorState message={error} />
      </Card>
    );
  }
  if (!data) {
    return (
      <Card>
        <EmptyState title="No uptime data" message="The uptime endpoint returned nothing." tone="nodata" />
      </Card>
    );
  }

  const scoped = data.monitors.filter((m) => scope === "all" || m.region === scope);
  const regionLabels = new Map(data.regions.map((r) => [r.region, r.label]));
  const respSeries = scoped
    .map((m) => m.responseSeries)
    .filter((s): s is number[] => Array.isArray(s) && s.length > 0);
  const respAvg = respSeries.length > 0 ? averageSeries(respSeries) : [];

  return (
    <>
      <style>{PAGE_STYLES}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Chip active={scope === "all"} onClick={() => setScope("all")}>
            All monitors
          </Chip>
          {data.regions.map((r) => (
            <Chip
              key={r.region}
              active={scope === r.region}
              onClick={() => setScope(r.region)}
              dot={TONE_DOT[worstTone(r.history)]}
            >
              {r.label}
            </Chip>
          ))}
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            padding: 3,
            borderRadius: 9,
            background: "var(--surface)",
            border: "1px solid var(--border)",
          }}
        >
          {(["24h", "7d", "90d"] as Range[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              style={{
                padding: "5px 12px",
                borderRadius: 7,
                border: "none",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontSize: 12,
                fontWeight: 600,
                background: range === r ? "var(--accent-bg)" : "transparent",
                color: range === r ? "var(--accent)" : "var(--fg-subtle)",
              }}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <SummaryStrip stats={buildSummary(data, range)} />

      {data.regions.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(data.regions.length, 3)}, minmax(0, 1fr))`,
            gap: 16,
          }}
        >
          {data.regions.map((r) => (
            <RegionCard key={r.region} region={r} />
          ))}
        </div>
      )}

      <MonitorsCard monitors={scoped} sla={data.slaTarget} range={range} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: respAvg.length > 0 ? "minmax(0, 1.6fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
          gap: 16,
          alignItems: "start",
        }}
      >
        {respAvg.length > 0 && <ResponseCard series={respAvg} />}
        <IncidentsCard
          incidents={data.incidents}
          activeIncidents={data.summary.activeIncidents}
          regionLabels={regionLabels}
        />
      </div>
    </>
  );
}
